import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { Plus, Trash2 } from 'lucide-react'
import {
  addException,
  removeException,
  todayIso,
  updateException,
} from '../lib/scheduling'
import type {
  SchedulingException,
  SchedulingExceptionOrdering,
  SchedulingExceptionWindow,
} from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type WindowKey = 'unmanned' | 'staffed' | 'ordering' | 'pickup'
type OverrideMode = 'inherit' | 'closed' | 'override'

const WINDOW_KEYS: Array<WindowKey> = [
  'unmanned',
  'staffed',
  'ordering',
  'pickup',
]

interface Props {
  value: Array<SchedulingException>
  onChange: (next: Array<SchedulingException>) => void
}

function modeOf(
  override: SchedulingExceptionWindow | SchedulingExceptionOrdering | undefined,
): OverrideMode {
  if (!override) return 'inherit'
  if (override.closed) return 'closed'
  return 'override'
}

export function ExceptionsEditor({ value, onChange }: Props) {
  const { t: tRestaurants } = useTranslation('restaurants')
  const [newDate, setNewDate] = useState('')
  const [showPast, setShowPast] = useState(false)

  const today = todayIso()
  const visible = showPast ? value : value.filter((e) => e.date >= today)
  const hiddenCount = value.length - visible.length

  const add = () => {
    const next = addException(value, newDate)
    if (next) {
      onChange(next)
      setNewDate('')
    }
  }

  const setWindow = (
    date: string,
    key: WindowKey,
    override:
      | SchedulingExceptionWindow
      | SchedulingExceptionOrdering
      | undefined,
  ) => {
    onChange(updateException(value, date, { [key]: override }))
  }

  const changeMode = (
    exception: SchedulingException,
    key: WindowKey,
    mode: OverrideMode,
  ) => {
    if (mode === 'inherit') {
      setWindow(exception.date, key, undefined)
    } else if (mode === 'closed') {
      setWindow(exception.date, key, { closed: true })
    } else if (key === 'ordering') {
      setWindow(exception.date, key, {
        opensLocal: '08:00',
        closesLocal: '11:00',
      })
    } else {
      setWindow(exception.date, key, { open: '11:00', close: '14:00' })
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{tRestaurants('scheduling.exceptions.title')}</CardTitle>
          <CardDescription>
            {tRestaurants('scheduling.exceptions.hint')}
          </CardDescription>
        </div>
        {hiddenCount > 0 || showPast ? (
          <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showPast}
              onCheckedChange={(v) => setShowPast(Boolean(v))}
            />
            {tRestaurants('scheduling.exceptions.showPast', {
              count: hiddenCount,
            })}
          </label>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="space-y-2">
            <Label>{tRestaurants('scheduling.exceptions.date')}</Label>
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-44"
            />
          </div>
          <Button
            onClick={add}
            disabled={!newDate || value.some((e) => e.date === newDate)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {tRestaurants('scheduling.exceptions.add')}
          </Button>
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {tRestaurants('scheduling.exceptions.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map((exception) => (
              <div key={exception.date} className="rounded-md border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-medium">
                    {new Date(`${exception.date}T00:00:00`).toLocaleDateString(
                      'de-AT',
                      {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      },
                    )}
                    {exception.date < today && (
                      <Badge variant="outline">
                        {tRestaurants('scheduling.exceptions.past')}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onChange(removeException(value, exception.date))
                    }
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {tRestaurants('scheduling.exceptions.remove')}
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {WINDOW_KEYS.map((key) => {
                    const override = exception[key]
                    const mode = modeOf(override)
                    return (
                      <div
                        key={key}
                        className="flex flex-wrap items-center gap-2"
                      >
                        <div className="w-40 shrink-0 text-sm">
                          {tRestaurants(
                            `scheduling.exceptions.window.${key}` as const,
                          )}
                        </div>
                        <Select
                          value={mode}
                          onValueChange={(v) =>
                            changeMode(exception, key, v as OverrideMode)
                          }
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">
                              {tRestaurants(
                                'scheduling.exceptions.mode.inherit',
                              )}
                            </SelectItem>
                            <SelectItem value="closed">
                              {tRestaurants(
                                'scheduling.exceptions.mode.closed',
                              )}
                            </SelectItem>
                            <SelectItem value="override">
                              {tRestaurants(
                                'scheduling.exceptions.mode.override',
                              )}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {mode === 'override' && key === 'ordering' && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={
                                (override as SchedulingExceptionOrdering)
                                  .opensLocal ?? ''
                              }
                              onChange={(e) =>
                                setWindow(exception.date, key, {
                                  ...(override as SchedulingExceptionOrdering),
                                  opensLocal: e.target.value,
                                })
                              }
                              className="w-28"
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                              type="time"
                              value={
                                (override as SchedulingExceptionOrdering)
                                  .closesLocal ?? ''
                              }
                              onChange={(e) =>
                                setWindow(exception.date, key, {
                                  ...(override as SchedulingExceptionOrdering),
                                  closesLocal: e.target.value || null,
                                })
                              }
                              className="w-28"
                            />
                          </div>
                        )}
                        {mode === 'override' && key !== 'ordering' && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={
                                (override as SchedulingExceptionWindow).open ??
                                ''
                              }
                              onChange={(e) =>
                                setWindow(exception.date, key, {
                                  ...(override as SchedulingExceptionWindow),
                                  open: e.target.value,
                                })
                              }
                              className="w-28"
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                              type="time"
                              value={
                                (override as SchedulingExceptionWindow).close ??
                                ''
                              }
                              onChange={(e) =>
                                setWindow(exception.date, key, {
                                  ...(override as SchedulingExceptionWindow),
                                  close: e.target.value,
                                })
                              }
                              className="w-28"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
